import { useMemo } from "react";
import { useWidgetViewState } from "@salestrak/ui";
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { parseWidgetFilters, resolveWidgetFilters } from "#app/widgets/dashboards/widget-filters";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import type { SortDirection } from "#app/lib/data";
import { combineWhereConditions } from "#app/lib/query-utils";
import type { Omittable } from "@salestrak/ui";

/**
 * The card's transient, per-viewer header filters — set by the header controls
 * (Only-Me toggle + advanced filter popover), read here, NEVER persisted (that's
 * `filters`/config). The persisted config query seeds the visible header filter;
 * after the header publishes `filterWhere`, it owns the query predicate for this
 * viewer/session so clear/tweak works without writing settings. Config-level
 * `showArchived`/`dateRange` still fold from persisted settings. `onlyMe` forces
 * the scope's current-user narrowing on for this card only; the advanced-query
 * `where` is folded centrally by `useScopedWhere`.
 */
export type EntityListTransientFilters = Omittable<{
  onlyMe?: boolean;
  filterWhere?: Record<string, unknown> | null;
}>;

/** Row ordering for the project list. */
export type EntityListSort = "created" | "updated" | "name";

/** Stable orderBy per sort — module-level so the query variables stay referentially
 *  stable across renders (an inline object would re-subscribe every render). */
const ORDER_BY: Record<EntityListSort, Record<string, SortDirection>> = {
  created: { creationDate: "desc" },
  updated: { lastUpdateDate: "desc" },
  name: { name: "asc" },
};

/**
 * The data layer for the `entity-list` (Project list) card: the ONE folded
 * `where` + `orderBy` its table pages over. Folds the card's Filters blob and
 * the ambient dashboard window exactly like the drilldown reader — the date
 * column follows the sort so the window narrows the same field the list is
 * ordered by — then AND-folds the card's search as a server `name contains`
 * predicate. The card never fetches rows itself: the shared `IssueTable` pages
 * every group through windowed server queries over this `where`, so the list
 * reaches EVERY project in scope — never a capped bulk chunk.
 *
 * Query ownership is split intentionally: before the header publishes, the persisted
 * config query stays folded into `base` so there is no unfiltered flash query. Once
 * `filterWhere` is defined in view-state (object = active, null = explicitly
 * cleared), the visible header owns the query predicate and `base` is rebuilt with
 * only config-level `showArchived`/`dateRange` retained. A card never hand-builds
 * the final scoped `where`.
 */
export function useEntityList(
  sort: EntityListSort,
  filters: unknown,
  search: string,
  transient?: EntityListTransientFilters,
): {
  where: Record<string, unknown> | undefined;
  orderBy: Record<string, SortDirection>;
} {
  const { state } = useWidgetViewState();
  const filterWhere = (state as EntityListTransientFilters).filterWhere;
  const parsedFilters = parseWidgetFilters(filters);
  const headerHasPublished = filterWhere !== undefined;
  const filtersForBase = headerHasPublished
    ? { ...parsedFilters, query: null }
    : parsedFilters;
  const { base, override } = resolveWidgetFilters(filtersForBase);
  const dateField = sort === "updated" ? ("lastUpdateDate" as const) : ("creationDate" as const);

  // Only-Me sets the per-card scope override on (leaving it off inherits the
  // dashboard toolbar's Only-Me); header advanced filters fold in `useScopedWhere`.
  const scopedOverride = transient?.onlyMe ? { ...override, onlyMe: true } : override;

  const { where: scopedWhere } = useScopedWhere(base, { dateField, override: scopedOverride });
  const trimmedSearch = search.trim();
  const where = useMemo(
    () =>
      trimmedSearch
        ? combineWhereConditions([scopedWhere, { name: { contains: trimmedSearch } }])
        : scopedWhere,
    [scopedWhere, trimmedSearch],
  );

  return { where, orderBy: ORDER_BY[sort] };
}

/**
 * The exact scoped project count for the card's title adornment — the same
 * server aggregation the metric card reduces (grouping by `stageId` is 1:1
 * with a project, so summing every bucket is the exact total), never a fetch.
 */
export function useEntityListTotal(
  sort: EntityListSort,
  filters: unknown,
): { total: number; loading: boolean } {
  const { where } = useEntityList(sort, filters, "");
  const { counts, ready } = useIssueGroupCounts({ where: where ?? {}, groupBy: "stageId" });
  const total = useMemo(
    () => Object.values(counts).reduce((sum, n) => sum + n, 0),
    [counts],
  );
  return { total, loading: !ready };
}
